// P3-010: Damage calculation system (GDD section 4.5.4)

import { SkillEffectType } from '@/shared/types/common';
import type { Match } from '../types/board';
import type { BattleAgent, ActiveBuff, DamageResult } from '../types/battle-internal';
import { chance } from '@/shared/utils/random';

/**
 * Combo multiplier table — indexed by [chainDepthIndex][weightTier].
 * chainDepthIndex = min(chainDepth - 1, 3)  →  0=round1, 1=round2, 2=round3, 3=round4+
 * weightTier = min(floor((tilesRemoved - 3) / 3), 3)  →  0=3-5, 1=6-8, 2=9-11, 3=12+
 */
export const COMBO_MULTIPLIER_TABLE: readonly (readonly number[])[] = [
  // chainDepth=1
  [1.00, 1.10, 1.20, 1.30],
  // chainDepth=2
  [1.25, 1.40, 1.55, 1.70],
  // chainDepth=3
  [1.55, 1.75, 1.95, 2.20],
  // chainDepth=4+
  [1.90, 2.15, 2.45, 2.80],
];

/** Look up combo multiplier from chain depth and tile count */
export function getComboMultiplier(chainDepth: number, tileCount: number): number {
  const depthIndex = Math.min(3, Math.max(0, chainDepth - 1));
  const weightTier = Math.min(3, Math.max(0, Math.floor((tileCount - 3) / 3)));
  return COMBO_MULTIPLIER_TABLE[depthIndex]![weightTier]!;
}

/**
 * Calculate damage for a single match.
 *
 * Formula (updated — cascade combo redesign):
 *   totalDamage = floor((baseDamage + agentBonus) * critMultiplier * comboMultiplier)
 *
 *   baseDamage = tileCount * 10
 *   agentBonus = floor(matchingAgentsATK * 0.15)
 *   critMultiplier = isCrit ? 1.5 : 1.0
 *   comboMultiplier = COMBO_MULTIPLIER_TABLE[chainDepth][weightTier]
 *
 * Then apply buff/debuff modifiers.
 */
export function calculateMatchDamage(
  match: Match,
  team: BattleAgent[],
  comboCount: number,
  buffs: ActiveBuff[],
  roundTileCount?: number,
): DamageResult {
  const tileCount = match.tiles.length;
  const element = match.element;

  // Base damage: tileCount * 10
  const baseDamage = tileCount * 10;

  // Agent bonus: floor(matchingAgentsATK * 0.15)
  const matchingAgents = team.filter((a) => a.elementAffinity === element);
  const matchingAgentsATK = matchingAgents.reduce((sum, a) => sum + a.attack, 0);
  const agentBonus = Math.floor(matchingAgentsATK * 0.15);

  // Crit check: average critRate of matching color agents
  const avgCritRate = matchingAgents.length > 0
    ? matchingAgents.reduce((sum, a) => sum + a.critRate, 0) / matchingAgents.length
    : 0;
  const isCrit = chance(avgCritRate);
  const critMultiplier = isCrit ? 1.5 : 1.0;

  // Combo multiplier (replaces flat comboBonus)
  const effectiveTileCount = roundTileCount ?? tileCount;
  const comboMultiplier = getComboMultiplier(comboCount, effectiveTileCount);

  // Calculate raw damage with multiplier
  let total = Math.floor((baseDamage + agentBonus) * critMultiplier * comboMultiplier);

  // Apply buff/debuff modifiers
  total = applyBuffModifiers(total, buffs);

  return {
    total: Math.max(0, total),
    base: baseDamage,
    agentBonus,
    comboBonus: 0, // deprecated — multiplier baked into total
    isCrit,
    matchElement: element,
    tileCount,
  };
}

/**
 * Apply all active buff/debuff modifiers to damage.
 */
function applyBuffModifiers(damage: number, buffs: ActiveBuff[]): number {
  let modified = damage;

  for (const buff of buffs) {
    switch (buff.type) {
      case SkillEffectType.BUFF_ATK:
        // ATK buff: increase damage by percentage
        if (buff.target === 'team' || buff.target === 'self') {
          modified = Math.floor(modified * (1 + buff.value));
        }
        break;

      case SkillEffectType.DEBUFF_ENEMY:
        // Enemy debuff: enemy takes more damage
        if (buff.target === 'enemy') {
          modified = Math.floor(modified * (1 + buff.value));
        }
        break;

      case SkillEffectType.BUFF_DEF:
        // DEF buff doesn't directly affect damage dealt
        break;

      default:
        break;
    }
  }

  return modified;
}

/**
 * Apply demand-change damage reduction.
 * Called separately from buff modifiers because it comes from enemy events.
 */
export function applyDemandChangeReduction(
  damage: number,
  enemyPhase: 'normal' | 'demand-change-light' | 'demand-change-heavy',
): number {
  switch (enemyPhase) {
    case 'demand-change-light':
      return Math.floor(damage * 0.85); // -15%
    case 'demand-change-heavy':
      return Math.floor(damage * 0.75); // -25%
    default:
      return damage;
  }
}

/**
 * Calculate total damage for all matches in a cascade step.
 */
export function calculateStepDamage(
  matches: Match[],
  team: BattleAgent[],
  comboCount: number,
  buffs: ActiveBuff[],
  enemyPhase: 'normal' | 'demand-change-light' | 'demand-change-heavy',
  roundTileCount?: number,
): { damages: DamageResult[]; totalDamage: number; critCount: number } {
  const damages: DamageResult[] = [];
  let totalDamage = 0;
  let critCount = 0;

  for (const match of matches) {
    const result = calculateMatchDamage(match, team, comboCount, buffs, roundTileCount);
    // Apply demand-change reduction
    const reducedTotal = applyDemandChangeReduction(result.total, enemyPhase);
    const finalResult = { ...result, total: reducedTotal };

    damages.push(finalResult);
    totalDamage += reducedTotal;
    if (finalResult.isCrit) critCount++;
  }

  return { damages, totalDamage, critCount };
}
